/** @jsxImportSource @emotion/react */
import type { FloorPlanRoom } from '@kaiizen/kai-floor-plan';
import {
  FloorPlanAssembler,
  type FloorPlanAssemblerHandle,
  FloorPlanEditor,
  type FloorPlanEditorHandle,
} from '@kaiizen/kai-floor-plan-editor';
import type { AssemblyRoom, OpeningView } from '@kaiizen/kai-floor-plan-editor-core';
import { useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';
import { useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router';

import { useRoomOpening } from '@/Client/Partials/FocusPanel/useRoomOpening';
import {
  type FloorPlanEditorDemoView,
  getFloorPlanEditorDemoUrl,
  parseFloorPlanEditorDemoPath,
  useEditorNavigationGuard,
  useNavigateToFloorPlanEditorDemo,
} from '@/Client/State';

import { DEMO_FLOOR_TYPES, DEMO_PROPERTY_ROOMS } from './floorPlanEditorDemoData';
import { DEFAULT_FLOOR_PLAN_EDITOR_DEMO_STYLES, type FloorPlanEditorDemoStyles } from './floorPlanEditorDemoStyles';

const VIEW_OPTIONS: { view: 'room' | 'floorPlan'; label: string }[] = [
  { view: 'room', label: 'Room' },
  { view: 'floorPlan', label: 'Floor plan' },
];

/** The room editor on one room, kept in the page's memory: its dirty state arms the unsaved-changes prompt. */
const DemoRoomEditor = ({ room, defaultView, onSave }: {
  room: FloorPlanRoom | undefined;
  defaultView?: OpeningView;
  onSave: (room: FloorPlanRoom) => void;
}) => {
  const editorRef = useRef<FloorPlanEditorHandle | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  useEditorNavigationGuard(hasUnsavedChanges, editorRef);
  return (
    <FloorPlanEditor
      ref={editorRef}
      defaultRoom={room}
      defaultView={defaultView}
      floorTypes={DEMO_FLOOR_TYPES}
      onDirtyChange={setHasUnsavedChanges}
      onSave={onSave}
      readOnly={false}
    />
  );
};

/** The floor plan view over the demo property: its dirty state arms the unsaved-changes prompt, and the floor plan editor's advanced flag decides its export menu. */
const DemoFloorPlan = ({ rooms, onSave, onOpenRoom }: {
  rooms: AssemblyRoom[];
  onSave: (changedRooms: AssemblyRoom[]) => void;
  onOpenRoom: (roomId: string, view: OpeningView) => void;
}) => {
  const { featureFlags } = useFeatureFlags();
  const assemblerRef = useRef<FloorPlanAssemblerHandle | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  useEditorNavigationGuard(hasUnsavedChanges, assemblerRef);
  return (
    <FloorPlanAssembler
      ref={assemblerRef}
      rooms={rooms}
      onSave={onSave}
      onOpenRoom={onOpenRoom}
      onDirtyChange={setHasUnsavedChanges}
      readOnly={false}
      advanced={featureFlags.floorPlanEditorAdvanced}
    />
  );
};

/**
 * The floor plan editors on their own, over sample data, with every save kept in this page's
 * memory only: the room editor on a default room, and the floor plan view over a sample
 * property whose "Edit room" opens a property room in the room editor. The view is the URL's
 * sub-path, and every switch goes through the unsaved-changes prompt, exactly as in a project.
 */
export const FloorPlanEditorDemo = ({ styles = DEFAULT_FLOOR_PLAN_EDITOR_DEMO_STYLES }: { styles?: FloorPlanEditorDemoStyles }) => {
  const demoView = parseFloorPlanEditorDemoPath(useLocation().pathname);
  const navigateToDemo = useNavigateToFloorPlanEditorDemo();
  const [demoRoom, setDemoRoom] = useState<FloorPlanRoom | undefined>(undefined);
  const [propertyRooms, setPropertyRooms] = useState(DEMO_PROPERTY_ROOMS);
  const openedRoomId = demoView.view === 'propertyRoom' ? demoView.roomId : undefined;
  const { holdOpening, openingFor } = useRoomOpening(openedRoomId);
  const openedRoom = propertyRooms.find(({ id }) => id === openedRoomId);

  if (demoView.view === 'propertyRoom' && !openedRoom) {
    return <Navigate to={getFloorPlanEditorDemoUrl({ view: 'floorPlan' })} replace />;
  }

  const shownViewId: FloorPlanEditorDemoView['view'] = demoView.view === 'propertyRoom' ? 'floorPlan' : demoView.view;

  return (
    <div css={styles.root} data-testid='floor-plan-editor-demo'>
      <div css={styles.toolbar}>
        <span css={styles.viewSwitch} role='group' aria-label='Floor plan editor demo view' data-testid='floor-plan-demo-view-switch'>
          {VIEW_OPTIONS.map(option => {
            const selected = option.view === shownViewId;
            return (
              <button
                key={option.view}
                type='button'
                css={[styles.viewOption, selected && styles.viewOptionSelected]}
                aria-pressed={selected}
                data-view={option.view}
                onClick={() => navigateToDemo({ view: option.view })}
              >
                {option.label}
              </button>
            );
          })}
        </span>
        {openedRoom && (
          <>
            <button type='button' css={styles.backButton} data-act='back-to-floor-plan' onClick={() => navigateToDemo({ view: 'floorPlan' })}>
              Back to the floor plan
            </button>
            <span css={styles.roomName}>{openedRoom.name}</span>
          </>
        )}
        <span css={styles.note}>Saves stay on this page: leaving the demo or reloading starts over.</span>
      </div>
      <div css={styles.editorHost}>
        {demoView.view === 'room' && <DemoRoomEditor key='demo-room' room={demoRoom} onSave={setDemoRoom} />}
        {demoView.view === 'floorPlan' && (
          <DemoFloorPlan
            rooms={propertyRooms}
            onSave={changedRooms => setPropertyRooms(rooms => rooms.map(room => changedRooms.find(changed => changed.id === room.id) ?? room))}
            onOpenRoom={(roomId, view) => {
              holdOpening(roomId, view);
              navigateToDemo({ view: 'propertyRoom', roomId });
            }}
          />
        )}
        {openedRoom && (
          <DemoRoomEditor
            key={openedRoom.id}
            room={openedRoom.room}
            defaultView={openingFor(openedRoom.id)}
            onSave={room => setPropertyRooms(rooms => rooms.map(candidate => candidate.id === openedRoom.id ? { ...candidate, room } : candidate))}
          />
        )}
      </div>
    </div>
  );
};
